import type { Event } from "@mvk/shared";
import { compareEventsByNearestStart, compareEventsForListing } from "@mvk/shared";
import { isApiEnabled } from "../api/config";
import { meApi } from "../api/me";
import { localClientState } from "../state/localClientState";
import { eventPackService } from "./eventPackService";

let cachedEvents: Event[] | null = null;

function sortEvents(list: Event[]): Event[] {
  return [...list].sort(compareEventsForListing);
}

async function loadFromApi(): Promise<Event[]> {
  if (!isApiEnabled()) {
    throw new Error("[eventsService] API is not configured");
  }

  const payload = await meApi.getEvents();
  cachedEvents = sortEvents(payload.events);
  localClientState.updateUserProfile({
    id: payload.user.id,
    fullName: payload.user.fullName,
    phone: payload.user.phone,
    email: payload.user.email,
    position: payload.user.position,
    companyName: payload.user.companyName,
  });
  return cachedEvents;
}

export const eventsService = {
  async getAll(): Promise<Event[]> {
    if (cachedEvents) return cachedEvents;
    return loadFromApi();
  },

  async getByStatus(status: Event["status"]): Promise<Event[]> {
    const events = await this.getAll();
    return events.filter((e) => e.status === status);
  },

  async getById(id: string): Promise<Event | undefined> {
    try {
      const events = await this.getAll();
      const found = events.find((e) => e.id === id);
      if (found) return found;
    } catch {
      /* fall through to pack */
    }
    const pack = await eventPackService.getCached(id);
    return pack?.event;
  },

  async getCurrentSuggested(): Promise<Event | undefined> {
    const events = await this.getAll();
    const active = events
      .filter((e) => e.status === "active")
      .sort(compareEventsByNearestStart);
    if (active.length) return active[0];

    const upcoming = events
      .filter((e) => e.status === "upcoming")
      .sort(compareEventsByNearestStart);
    if (upcoming.length) return upcoming[0];

    const past = events
      .filter((e) => e.status === "past")
      .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime());
    return past[0];
  },

  /**
   * Resolve which event to reopen on mini-app launch.
   * Does NOT fall back to a random "suggested" event when the user has many
   * accessible exhibitions — that would steal the picker on first open.
   *
   * Priority:
   * 1) start_param from MAX (if still accessible)
   * 2) last selected event still in access list
   * 3) single accessible event → auto-open
   * 4) single actual (non-past) event → auto-open
   */
  async resolveLaunchEvent(preferredId?: string | null): Promise<Event | null> {
    const events = await this.getAll();
    if (!events.length) return null;

    if (preferredId) {
      const fromStart = events.find((e) => e.id === preferredId);
      if (fromStart) return fromStart;
    }

    const savedId = this.getSelectedId();
    if (savedId) {
      const saved = events.find((e) => e.id === savedId);
      if (saved) return saved;
    }

    if (events.length === 1) return events[0]!;

    const actual = events.filter((e) => e.status !== "past");
    if (actual.length === 1) return actual[0]!;

    return null;
  },

  setSelected(eventId: string): void {
    localClientState.setSelectedEventId(eventId);
  },

  getSelectedId(): string | null {
    return localClientState.getSelectedEventId();
  },

  clearCache(): void {
    cachedEvents = null;
  },
};
